import { RESOURCE_TYPES, type BiomeName } from "@eldrun/content";
import { hash32 } from "@eldrun/shared";
import { BufferGeometry, Color, DynamicDrawUsage, InstancedBufferAttribute, InstancedMesh, Matrix4, Quaternion, Vector3, type Object3D, type ShaderMaterial, type Vector4 } from "three";
import { BIOME_NAMES, CHUNK_SIZE, PLAN_SIDE, PLAN_SPACING, WORLD_HALF, type ResourceSet } from "@eldrun/worldgen";
import { models, PALETTE_ATTRIBUTES, type ModelLibrary } from "../render/model-library.ts";
import { BLOB_LIMIT_LOW, BLOB_RANGE, selectBlobs, type BlobCaster, type BlobShadows } from "../render/blob-shadows.ts";
import type { DebrisSource } from "./debris.ts";
import type { DissolvePart } from "./dissolves.ts";
import { QUALITY } from "../render/quality.ts";
import { resourceMeshes, type ResourceMeshes } from "../render/resource-meshes.ts";
import { withFarFade, withPalettes } from "../render/shaders/foliage.ts";

/** Distâncias (m) de cada LOD de recurso: detalhado, silhueta e nada. */
export const RESOURCE_LOD = { near: 110, far: 420 } as const;
/**
 * Troca sem pipocar (pontilhado na tela, `withFarFade`): a malha de perto sai e a silhueta entra nos
 * últimos `blend` m antes de `near`, pixel a pixel; a silhueta (e o recurso pequeno) some entre `fadeOut`
 * e 1 vez o alcance. Por instância, na GPU, a cada quadro.
 */
export const RESOURCE_FADE = { blend: 10, fadeOut: 0.85 } as const;
/** Recursos pequenos só aparecem de perto. */
const SMALL = new Set(["galhada", "moita", "cardume", "barreiro"]);
const SMALL_DISTANCE = 90;
/** A lista é refeita por célula de 32 m: cada LOD leva as instâncias até meia diagonal além da faixa dele. */
const CELL = CHUNK_SIZE / 4;
const SLACK = CELL * Math.SQRT2;
const WATER_VISUAL_Y = -0.3;

/**
 * Paleta das árvores por bioma (F42, doc 08): floresta e costa na Normal, charneca na Dry, montanhas
 * na Cold e pântano na Dry (verde-oliva turvo, diferente da floresta). Modelo sem a paleta do bioma
 * usa a sua padrão. Só visual: o bioma vem da grade do plano no cliente, fora de qualquer hash.
 */
export const BIOME_PALETTE: Readonly<Record<BiomeName, string>> = { mar: "normal", praia: "normal", mangue: "dry", mata: "normal", cerrado: "dry", serra: "cold" };

/**
 * Recursos cuja paleta varia por instância (pelo id), não pelo bioma: cerejeiras rosa ou amarelas
 * (doc 05, destaques da mata; antes o ipê era pintado pela cor da instância, tronco junto).
 */
export const PALETTE_BY_ID: ReadonlySet<string> = new Set(["ipe"]);

/** Índice do bioma (`BIOME`) num ponto do mundo. */
export type BiomeAt = (x: number, z: number) => number;

/** Bioma pela grade do plano (8 m), a mesma que o cliente já tem do worldgen. */
export function planBiomes(biome: Uint8Array): BiomeAt {
  const cell = (v: number) => Math.min(PLAN_SIDE - 1, Math.max(0, Math.round((v + WORLD_HALF) / PLAN_SPACING)));
  return (x, z) => biome[cell(z) * PLAN_SIDE + cell(x)] ?? 0;
}

export interface ResourceLayerOptions {
  library?: ModelLibrary;
  /** Sem ele, toda instância usa a paleta padrão do modelo. */
  biomeAt?: BiomeAt;
}

/** Materiais da troca de LOD: o de perto, o da silhueta e o dos recursos pequenos (o mesmo programa). */
interface LodMaterials {
  near: ShaderMaterial;
  far: ShaderMaterial;
  small: ShaderMaterial;
}

/** Malhas de um tipo: uma por variante de perto e a silhueta de longe, criadas na primeira instância. */
interface TypeMeshes {
  source: ResourceMeshes;
  /** O material sem a troca de LOD (o dissolve, F95, parte dele). */
  material: ShaderMaterial;
  lod: LodMaterials;
  near: (InstancedMesh | undefined)[];
  far: InstancedMesh | undefined;
  /** Instâncias por variante (capacidade das malhas de perto); a de longe cabe o tipo inteiro. */
  counts: number[];
  total: number;
}

const m4 = new Matrix4();
const q = new Quaternion();
const v = new Vector3();
const s = new Vector3();
const up = new Vector3(0, 1, 0);
const color = new Color();

/** As três variantes da troca de LOD de `material`; `update` põe as faixas com a distância de visão. */
function lodMaterials(material: ShaderMaterial): LodMaterials {
  return { near: withFarFade(material, 0, 0), far: withFarFade(material, 0, 0, [0, 0]), small: withFarFade(material, 0, 0) };
}

const bandOf = (material: ShaderMaterial) => material.uniforms["uFarFade"]!.value as Vector4;

/** Variante de malha de uma instância: estável pelo id, então todo jogador vê o mesmo modelo. */
export function variantOf(id: number, variants: number): number {
  return variants > 1 ? hash32(id, 0x5eed) % variants : 0;
}

/** Índice da paleta do bioma entre as do modelo (0, a padrão, se ele não tiver). */
export function paletteOf(biome: number, palettes: readonly string[]): number {
  const name = BIOME_PALETTE[BIOME_NAMES[biome] ?? "mata"];
  return Math.max(0, palettes.indexOf(name));
}

/**
 * Recursos da ilha desenhados por instâncias (F37/F42): por tipo, uma InstancedMesh de perto por
 * variante de modelo e uma silhueta de longe, criadas só quando alguma instância precisa (combinação
 * vazia não existe nem custa draw call). A paleta (Normal/Dry/Cold) é escolhida por instância no
 * shader, sem malha extra. A lista de instâncias é refeita quando o jogador troca de célula ou
 * quando algum recurso some/renasce, não a cada quadro.
 */
export class ResourceLayer {
  private readonly set: ResourceSet;
  private readonly root: Object3D;
  private readonly baseMaterial: ShaderMaterial;
  private paletteMaterial: ShaderMaterial | undefined;
  private readonly lod: LodMaterials;
  private paletteLod: LodMaterials | undefined;
  private readonly types: TypeMeshes[];
  /** Variante e paleta de cada instância do conjunto (índice k). */
  private readonly variant: Uint8Array;
  private readonly palette: Uint8Array;
  /** Geometrias próprias (com o atributo de instância `palette`), liberadas no `dispose`. */
  private readonly owned: BufferGeometry[] = [];
  private readonly removed = new Set<number>();
  private lastCell = "";
  private dirty = true;
  /** A câmera da última vez que as instâncias foram refeitas (o que está desenhado, para o dissolve, F95). */
  private cameraX = 0;
  private cameraZ = 0;
  private shadowCell = "";
  private readonly shadowCandidates: (BlobCaster & { resourceId: number })[] = [];
  private readonly shadowActive: BlobCaster[] = [];
  private readonly shadowChosen: BlobCaster[] = [];

  constructor(root: Object3D, set: ResourceSet, material: ShaderMaterial, options: ResourceLayerOptions = {}) {
    this.root = root;
    this.set = set;
    this.baseMaterial = material;
    this.lod = lodMaterials(material);
    const library = options.library ?? models.library;
    this.types = RESOURCE_TYPES.map((type) => {
      const source = resourceMeshes(type.id, library);
      const palettes = source.palettes.length > 1;
      return { source, material: palettes ? this.palettes() : material, lod: palettes ? this.paletteLods() : this.lod, near: [], far: undefined, counts: source.near.map(() => 0), total: 0 };
    });
    this.variant = new Uint8Array(set.count);
    this.palette = new Uint8Array(set.count);
    for (let k = 0; k < set.count; k++) {
      const typeIndex = set.types[k] as number;
      const type = this.types[typeIndex]!;
      const id = set.ids[k] as number;
      const variant = variantOf(id, type.counts.length);
      this.variant[k] = variant;
      type.counts[variant]! += 1;
      type.total += 1;
      const palettes = type.source.palettes;
      if (palettes.length < 2) continue;
      if (PALETTE_BY_ID.has(RESOURCE_TYPES[typeIndex]!.id)) this.palette[k] = hash32(id, 0xb105) % palettes.length;
      else if (options.biomeAt) this.palette[k] = paletteOf(options.biomeAt(set.x[k] as number, set.z[k] as number), palettes);
    }
  }

  private palettes(): ShaderMaterial {
    this.paletteMaterial ??= withPalettes(this.baseMaterial);
    return this.paletteMaterial;
  }

  /** As variantes com paleta dividem as faixas (`uFarFade`) das sem paleta. */
  private paletteLods(): LodMaterials {
    this.paletteLod ??= { near: withPalettes(this.lod.near), far: withPalettes(this.lod.far), small: withPalettes(this.lod.small) };
    return this.paletteLod;
  }

  /** Malha de perto da variante (`variant` ≥ 0) ou a de longe (−1), criada na primeira vez. */
  private mesh(typeIndex: number, variant: number): InstancedMesh {
    const type = this.types[typeIndex]!;
    const existing = variant < 0 ? type.far : type.near[variant];
    if (existing) return existing;
    const capacity = variant < 0 ? type.total : (type.counts[variant] as number);
    let geometry = variant < 0 ? type.source.far : type.source.near[variant]!;
    if (type.source.palettes.length > 1) {
      // Geometria própria que divide os buffers do modelo e ganha a paleta por instância.
      const own = new BufferGeometry();
      for (const [name, attribute] of Object.entries(geometry.attributes)) own.setAttribute(name, attribute);
      own.setIndex(geometry.index);
      own.boundingSphere = geometry.boundingSphere;
      own.setAttribute("palette", new InstancedBufferAttribute(new Float32Array(capacity), 1).setUsage(DynamicDrawUsage));
      this.owned.push(own);
      geometry = own;
    }
    const id = RESOURCE_TYPES[typeIndex]!.id;
    const mesh = new InstancedMesh(geometry, variant < 0 ? type.lod.far : SMALL.has(id) ? type.lod.small : type.lod.near, capacity);
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    mesh.count = 0;
    // As instâncias cobrem a ilha toda; o culling é por distância (abaixo), não por frustum.
    mesh.frustumCulled = false;
    // A silhueta só aparece além da área da sombra do sol (~76 m); na folga da lista ela a dobraria.
    if (variant < 0) mesh.userData["noShadow"] = true;
    mesh.name = variant < 0 ? `recurso-${id}-far` : `recurso-${id}-${variant}-near`;
    this.root.add(mesh);
    if (variant < 0) type.far = mesh;
    else type.near[variant] = mesh;
    return mesh;
  }

  private *all(): Iterable<InstancedMesh> {
    for (const type of this.types) {
      for (const mesh of type.near) if (mesh) yield mesh;
      if (type.far) yield type.far;
    }
  }

  /**
   * Ancoragem suave das árvores/rochas próximas (pesquisa 11), sem malha nem luz extra. Só oito
   * ofertas de prioridade inferior aos atores; o Baixo reserva todos os projetores aos atores.
   * A varredura do conjunto só ocorre ao trocar de célula de 16 m. Remoções são lidas a cada quadro.
   */
  castBlobs(sink: Pick<BlobShadows, "add">, cameraX: number, cameraZ: number): void {
    if (QUALITY.blobs <= BLOB_LIMIT_LOW) return;
    const cellX = Math.floor(cameraX / 16);
    const cellZ = Math.floor(cameraZ / 16);
    const cell = `${cellX},${cellZ}`;
    if (cell !== this.shadowCell) {
      this.shadowCell = cell;
      this.shadowCandidates.length = 0;
      const set = this.set;
      // Distância ao centro da célula + sua meia diagonal cobre todo movimento dentro dela.
      const reach = BLOB_RANGE + Math.SQRT2 * 8;
      for (let k = 0; k < set.count; k++) {
        const type = RESOURCE_TYPES[set.types[k] as number]!;
        if (!type.collider || type.waterDepth) continue;
        const x = set.x[k] as number;
        const z = set.z[k] as number;
        if ((x - (cellX * 16 + 8)) ** 2 + (z - (cellZ * 16 + 8)) ** 2 > reach * reach) continue;
        const [radius, height] = type.collider;
        const scale = set.scale[k] as number;
        const resourceId = set.ids[k] as number;
        this.shadowCandidates.push({ id: `resource:${resourceId}`, resourceId, x, y: set.y[k] as number, z, radius: Math.min(3.6, Math.max(radius * 1.2, height * 0.2)) * scale, priority: -1 });
      }
    }
    this.shadowActive.length = 0;
    for (const caster of this.shadowCandidates) if (!this.removed.has(caster.resourceId)) this.shadowActive.push(caster);
    selectBlobs(this.shadowActive, cameraX, cameraZ, 8, BLOB_RANGE, this.shadowChosen);
    for (const caster of this.shadowChosen) sink.add(caster.id, caster.x, caster.y, caster.z, caster.radius, caster.priority, 0.65);
  }

  setRemoved(id: number, removed: boolean): void {
    if (removed === this.removed.has(id)) return;
    if (removed) this.removed.add(id);
    else this.removed.delete(id);
    this.dirty = true;
  }

  isRemoved(id: number): boolean {
    return this.removed.has(id);
  }

  /**
   * O modelo de perto da instância `k` do conjunto, na matriz dela e com o atributo de cor da paleta
   * dela: de onde sai o entulho quando ela quebra (F90). A mesma conta do `update`.
   */
  debrisSource(k: number): DebrisSource | undefined {
    const set = this.set;
    const type = RESOURCE_TYPES[set.types[k] as number];
    const entry = type && this.types[type.index];
    const geometry = entry?.source.near[this.variant[k] as number] ?? entry?.source.near[0];
    if (!type || !geometry) return undefined;
    const scale = set.scale[k] as number;
    const y = type.waterDepth ? Math.max(set.y[k] as number, WATER_VISUAL_Y) : (set.y[k] as number);
    const matrix = new Matrix4().compose(new Vector3(set.x[k] as number, y, set.z[k] as number), new Quaternion().setFromAxisAngle(up, set.yaw[k] as number), new Vector3(scale, scale, scale));
    const palette = this.palette[k] as number;
    return { geometry, matrix, color: PALETTE_ATTRIBUTES[palette] ?? "color" };
  }

  /**
   * Variação de ±6 % por instância. Sem modelos, o ipê procedural (copa creme) ainda alterna rosa e
   * amarelo pela cor da instância; com modelos, a cor vem da paleta.
   */
  private tone(k: number, out: Color): Color {
    const set = this.set;
    const type = RESOURCE_TYPES[set.types[k] as number]!;
    const h = (((set.ids[k] as number) * 2654435761) >>> 0) / 4294967296;
    if (type.id === "ipe" && this.types[type.index]!.source.palettes.length < 2) return out.set(h > 0.5 ? "#E0457B" : "#F2C230");
    return out.setScalar(0.94 + h * 0.12);
  }

  /**
   * A instância `k` como foi desenhada no último `update`, para o dissolve (F95): a malha de perto da
   * variante ou a silhueta de longe, o material do tipo, a matriz, o atributo de cor da paleta e a cor da
   * instância. Nada se ela estava fora do alcance (ou já removida).
   */
  dissolvePart(k: number): DissolvePart | undefined {
    const set = this.set;
    const type = RESOURCE_TYPES[set.types[k] as number];
    const entry = type && this.types[type.index];
    if (!type || !entry || this.removed.has(set.ids[k] as number)) return undefined;
    const d2 = ((set.x[k] as number) - this.cameraX) ** 2 + ((set.z[k] as number) - this.cameraZ) ** 2;
    const small = SMALL.has(type.id);
    if ((small && d2 > (SMALL_DISTANCE * QUALITY.distance) ** 2) || d2 > (RESOURCE_LOD.far * QUALITY.distance) ** 2) return undefined;
    // Na faixa da troca, a metade de perto fica com a malha de perto.
    const near = d2 <= (RESOURCE_LOD.near - RESOURCE_FADE.blend / 2) ** 2 || small;
    const geometry = near ? entry.source.near[this.variant[k] as number] : entry.source.far;
    const source = this.debrisSource(k);
    if (!geometry || !source) return undefined;
    return { geometry, matrix: source.matrix, material: entry.material, colorAttribute: source.color ?? "color", color: this.tone(k, new Color()) };
  }

  /** Refaz as instâncias se a câmera mudou de célula (32 m), a distância de visão mudou ou algo mudou. */
  update(cameraX: number, cameraZ: number): void {
    const cell = `${Math.floor((cameraX + WORLD_HALF) / (CHUNK_SIZE / 4))},${Math.floor((cameraZ + WORLD_HALF) / (CHUNK_SIZE / 4))},${QUALITY.distance}`;
    if (!this.dirty && cell === this.lastCell) return;
    this.lastCell = cell;
    this.dirty = false;
    this.cameraX = cameraX;
    this.cameraZ = cameraZ;
    for (const mesh of this.all()) mesh.count = 0;
    // Distância de visão das Opções (F30) multiplica o alcance de desenho; a troca de perto, não.
    const far = RESOURCE_LOD.far * QUALITY.distance;
    const smallReach = SMALL_DISTANCE * QUALITY.distance;
    const blendStart = RESOURCE_LOD.near - RESOURCE_FADE.blend;
    for (const lod of [this.lod, this.paletteLod]) {
      if (!lod) continue;
      bandOf(lod.near).set(-2, -1, blendStart, RESOURCE_LOD.near);
      bandOf(lod.far).set(blendStart, RESOURCE_LOD.near, far * RESOURCE_FADE.fadeOut, far);
      bandOf(lod.small).set(-2, -1, smallReach * RESOURCE_FADE.fadeOut, smallReach);
    }
    // As listas: cada LOD com as instâncias da sua faixa, mais a folga da célula (o shader decide o resto).
    const nearMax = (RESOURCE_LOD.near + SLACK) ** 2;
    const farMin = Math.max(0, blendStart - SLACK) ** 2;
    const farMax = (far + SLACK) ** 2;
    const smallMax = (smallReach + SLACK) ** 2;
    const set = this.set;
    for (let k = 0; k < set.count; k++) {
      const id = set.ids[k] as number;
      if (this.removed.has(id)) continue;
      const type = RESOURCE_TYPES[set.types[k] as number]!;
      const dx = (set.x[k] as number) - cameraX;
      const dz = (set.z[k] as number) - cameraZ;
      const d2 = dx * dx + dz * dz;
      const small = SMALL.has(type.id);
      const nearList = small ? d2 <= smallMax : d2 <= nearMax;
      const farList = !small && d2 >= farMin && d2 <= farMax;
      if (!nearList && !farList) continue;
      const scale = set.scale[k] as number;
      // Recursos aquáticos ficam na coluna d'água, logo abaixo da superfície (y = nível do mar).
      const y = type.waterDepth ? Math.max(set.y[k] as number, WATER_VISUAL_Y) : (set.y[k] as number);
      m4.compose(v.set(set.x[k] as number, y, set.z[k] as number), q.setFromAxisAngle(up, set.yaw[k] as number), s.set(scale, scale, scale));
      this.tone(k, color);
      if (nearList) this.place(this.mesh(type.index, this.variant[k] as number), k);
      if (farList) this.place(this.mesh(type.index, -1), k);
    }
    for (const mesh of this.all()) {
      // Malha sem instância sai da lista de desenho (nem estado de GPU por quadro).
      mesh.visible = mesh.count > 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      const palette = mesh.geometry.getAttribute("palette");
      if (palette) palette.needsUpdate = true;
    }
  }

  /** Põe a instância `k` (matriz `m4`, cor `color`) no fim da malha. */
  private place(mesh: InstancedMesh, k: number): void {
    const index = mesh.count++;
    mesh.setMatrixAt(index, m4);
    mesh.setColorAt(index, color);
    const palette = mesh.geometry.getAttribute("palette");
    if (palette) (palette.array as Float32Array)[index] = this.palette[k] as number;
  }

  get drawn(): number {
    let n = 0;
    for (const mesh of this.all()) n += mesh.count;
    return n;
  }

  /** Malhas criadas até agora (cada uma com instância é um draw call). */
  get meshes(): number {
    return [...this.all()].length;
  }

  dispose(): void {
    for (const mesh of this.all()) {
      mesh.removeFromParent();
      mesh.dispose();
    }
    // Libera também os buffers divididos com a biblioteca: o three os sobe de novo se outra camada usar.
    for (const geometry of this.owned) geometry.dispose();
    this.paletteMaterial?.dispose();
    for (const lod of [this.lod, this.paletteLod]) for (const material of lod ? Object.values(lod) : []) material.dispose();
  }
}
